import type { ReactElement, ReactNode } from 'react';
import type { DotItemDotProps, LabelProps } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import { chartColor, type ChartColorKey } from '@/lib/chart-colors';
import type { PartialEdges } from '@/lib/partial-buckets';

/** Dash of the partial segment (CHART-02). */
export const PARTIAL_DASH = '5 5';

/** Texts next to the first/last partial point (from `partialLabel`). */
export type PartialLabels = { first?: string; last?: string };

const LABEL_GAP = 14;
/** Below this distance from the top the label goes under the point. */
const LABEL_MIN_TOP = 20;

/** Partial label text, with a halo so it stays legible over the line. */
const LABEL_TEXT_PROPS = {
  fontSize: 10,
  fontWeight: 500,
  fill: 'hsl(var(--muted-foreground))',
  stroke: 'hsl(var(--background))',
  strokeWidth: 3,
  paintOrder: 'stroke',
} as const;

const isPartialRow = (payload: unknown): boolean =>
  typeof payload === 'object' &&
  payload !== null &&
  'partial' in payload &&
  payload.partial === true;

/**
 * Dot renderer of the dashed series: a hollow ring on each partial bucket
 * and, next to it, its label (CHART-02, CHART-03). The first bucket's label
 * grows to the right and the last one's to the left, so neither leaves the
 * plot.
 */
export function partialDot(color: string, labels: PartialLabels) {
  function PartialDot({ cx, cy, index, payload, points }: DotItemDotProps) {
    if (cx === undefined || cy === undefined || !isPartialRow(payload)) {
      return <g key={index} />;
    }
    const isFirst = index === 0 && points.length > 1;
    const label = isFirst ? labels.first : labels.last;
    const labelY = cy < LABEL_MIN_TOP ? cy + LABEL_GAP + 8 : cy - LABEL_GAP;
    return (
      <g key={index}>
        <circle
          data-chart-partial-dot
          cx={cx}
          cy={cy}
          r={4}
          fill="hsl(var(--background))"
          stroke={color}
          strokeWidth={2}
        />
        {label ? (
          <text
            data-chart-partial-label
            x={cx}
            y={labelY}
            textAnchor={isFirst ? 'start' : 'end'}
            {...LABEL_TEXT_PROPS}
          >
            {label}
          </text>
        ) : null}
      </g>
    );
  }
  return PartialDot;
}

const COLOR_KEYS: ChartColorKey[] = [1, 2, 3];

/** Id of the diagonal-stripe pattern of a chart color (CHART-02, bars). */
export const hatchPatternId = (colorKey: ChartColorKey) =>
  `bar-hatch-${colorKey}`;

/**
 * `<defs>` shared by the chart shells: one hatch pattern per chart color,
 * diagonal stripes in that color over a light tint of it.
 */
export function ChartPartialDefs() {
  return (
    <defs>
      {COLOR_KEYS.map((key) => (
        <pattern
          key={key}
          id={hatchPatternId(key)}
          patternUnits="userSpaceOnUse"
          width={6}
          height={6}
          patternTransform="rotate(45)"
        >
          <rect width={6} height={6} fill={chartColor(key)} fillOpacity={0.2} />
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={6}
            stroke={chartColor(key)}
            strokeWidth={3}
          />
        </pattern>
      ))}
    </defs>
  );
}

/**
 * Label renderer of a bar series: the partial bar's label above it
 * (CHART-03), aligned like {@link partialDot}'s.
 */
export function partialBarLabel(
  partialIndexes: readonly number[],
  labels: PartialLabels
) {
  return function PartialBarLabel({
    index,
    viewBox,
  }: LabelProps): ReactElement {
    if (
      index === undefined ||
      !partialIndexes.includes(index) ||
      !viewBox ||
      !('width' in viewBox)
    ) {
      return <g />;
    }
    const isFirst = index === 0;
    const label = isFirst ? labels.first : labels.last;
    if (!label) return <g />;
    const { x = 0, y = 0, width = 0 } = viewBox;
    return (
      <text
        data-chart-partial-label
        x={isFirst ? x : x + width}
        y={Math.max(y - 6, 10)}
        textAnchor={isFirst ? 'start' : 'end'}
        {...LABEL_TEXT_PROPS}
      >
        {label}
      </text>
    );
  };
}

/** Swatch of the legend item: a dashed stroke (lines) or a hatched square (bars). */
function PartialSwatch({ variant }: { variant: 'dash' | 'hatch' }) {
  const color = 'hsl(var(--muted-foreground))';
  return (
    <svg aria-hidden width={16} height={10} viewBox="0 0 16 10">
      {variant === 'dash' ? (
        <line
          x1={0}
          y1={5}
          x2={16}
          y2={5}
          stroke={color}
          strokeWidth={2}
          strokeDasharray="4 3"
        />
      ) : (
        <g stroke={color} strokeWidth={1.5}>
          <rect x={3} y={0} width={10} height={10} fill="none" />
          <line x1={3} y1={6} x2={9} y2={0} />
          <line x1={3} y1={10} x2={13} y2={0} />
          <line x1={7} y1={10} x2={13} y2={4} />
        </g>
      )}
    </svg>
  );
}

/**
 * Legend item of the partial bucket (CHART-04): "Em andamento", or
 * "Período incompleto" when only the first bucket is partial. Nothing
 * without a partial bucket.
 */
export function ChartPartialLegend({
  edges,
  variant,
}: {
  edges: PartialEdges;
  variant: 'dash' | 'hatch';
}): ReactNode {
  const { LL } = useI18nContext();
  if (!edges.first && !edges.last) return null;
  return (
    <div
      data-chart-partial-legend
      className="flex items-center gap-1.5 pt-2 text-xs text-muted-foreground"
    >
      <PartialSwatch variant={variant} />
      {edges.last
        ? LL.chartPartial.legendOngoing()
        : LL.chartPartial.legendIncomplete()}
    </div>
  );
}

export const hasPartialEdge = (edges: PartialEdges | undefined): boolean =>
  Boolean(edges && (edges.first || edges.last));
